import { useMemo, useState } from 'react';

import {
  applySearchCriteriaUrlState,
  clearAllSearchCriteria,
  clearAllSearchCriteriaFilters,
  clearSearchCriteriaFilter,
  clearSearchCriteriaQuery,
  createInitialSearchCriteriaState,
  getAppliedCriteria,
  setSearchCriteriaDraftQuery,
  setSearchCriteriaFilterValues,
  submitSearchCriteriaQuery,
  toggleSearchCriteriaFilterValue,
} from './state';
import type { UseSearchCriteriaOptions, UseSearchCriteriaStateResult } from './types';

export const useSearchCriteria = <FilterName extends string>(
  options: UseSearchCriteriaOptions<FilterName>
): UseSearchCriteriaStateResult<FilterName> => {
  const filterNames = useMemo(
    () => Object.keys(options.filters) as FilterName[],
    [options.filters]
  );

  const [criteriaState, setCriteriaState] = useState(() =>
    createInitialSearchCriteriaState(options.filters, filterNames)
  );

  const setFilterValues = (filterName: FilterName, values: string[]) => {
    setCriteriaState((currentState) =>
      setSearchCriteriaFilterValues(currentState, options.filters, filterName, values)
    );
  };

  const toggleFilterValue = (filterName: FilterName, value: string) => {
    setCriteriaState((currentState) =>
      toggleSearchCriteriaFilterValue(currentState, options.filters, filterName, value)
    );
  };

  const clearFilter = (filterName: FilterName) => {
    setCriteriaState((currentState) => clearSearchCriteriaFilter(currentState, filterName));
  };

  const clearQuery = () => {
    setCriteriaState((currentState) => clearSearchCriteriaQuery(currentState));
  };

  const clearAllFilters = () => {
    setCriteriaState((currentState) => clearAllSearchCriteriaFilters(currentState, filterNames));
  };

  const clearAll = () => {
    setCriteriaState((currentState) => clearAllSearchCriteria(currentState, filterNames));
  };

  const submitQuery = (nextQuery = criteriaState.queryDraft) => {
    setCriteriaState((currentState) =>
      submitSearchCriteriaQuery(currentState, filterNames, nextQuery)
    );
  };

  const applyUrlState = (urlState: { query: string; filters: Record<FilterName, string[]> }) => {
    setCriteriaState((currentState) => applySearchCriteriaUrlState(currentState, urlState));
  };

  const appliedCriteria = useMemo(() => getAppliedCriteria(criteriaState), [criteriaState]);

  return {
    filterNames,
    query: criteriaState.query,
    queryDraft: criteriaState.queryDraft,
    selectedFilters: criteriaState.selectedFilters,
    appliedCriteria,
    setQueryDraft: (value) =>
      setCriteriaState((currentState) => setSearchCriteriaDraftQuery(currentState, value)),
    setFilterValues,
    toggleFilterValue,
    clearFilter,
    clearQuery,
    clearAllFilters,
    clearAll,
    submitQuery,
    applyUrlState,
  };
};
